import { useEffect, useMemo, useState } from "react";

import { PoolName } from "@/shared/constants/pools";
import { TradingChartData } from "@/shared/types/chart-risk";
import { request } from "@/shared/utils/request";

import { Token } from "../constants";
import { uniswapPoolWethUsdcArbitrum } from "../constants/pools/arbitrum";
import { getTokensByPoolName } from "../utils/get-tokens-by-pool-name";

interface Candle {
  baseCurrency: string;
  close: string;
  frame: string;
  high: string;
  low: string;
  open: string;
  openTimestamp: number;
  quoteCurrency: string;
  volumeBuy: number;
  volumeSell: number;
}

interface SwapCandlesRes {
  points: [number, Candle][];
}

const DAY_S = 24 * 60 * 60;

const getPoolInfoByQuoteToken = (token: Token) => {
  if (token === Token.WETH)
    return { poolAddress: uniswapPoolWethUsdcArbitrum.address, chainId: 2 };
  return null;
};

interface Props {
  data?: TradingChartData;
  poolName: PoolName;
}

export function useTradingDataWithRates({ poolName, data }: Props) {
  const quoteToken = getTokensByPoolName(poolName)[1];
  const poolInfo = useMemo(
    () => getPoolInfoByQuoteToken(quoteToken),
    [quoteToken],
  );

  const [rates, setRates] = useState<{ rate: number; timestamp: number }[]>();

  useEffect(() => {
    if (!data || !data[0]) return;
    if (poolInfo) {
      const fromUnixS = data[0].timestamp - DAY_S;
      const fetch = async () => {
        const response = await request<SwapCandlesRes>(
          `chain/${poolInfo.chainId}/pool/${poolInfo.poolAddress}/swapCandles?frame=OneDay&fromUnixS=${fromUnixS}`,
        );
        const rates = response.points.map((point) => ({
          rate: Number(point[1].close),
          timestamp: point[1].openTimestamp,
        }));
        setRates(rates);
      };
      fetch();
      return;
    }
    setRates(undefined);
  }, [data, poolInfo]);

  const tradingDataWithRates =
    rates &&
    data?.map((point, index) => {
      const rate = rates[index]?.rate || 1;
      return {
        ...point,
        closePrice: point.closePrice * rate,
        buyVolume: point.buyVolume * rate,
        sellVolume: point.sellVolume * rate,
        quoteDebt: point.quoteDebt * rate,
        baseDebt: point.baseDebt * rate,
        liquidatedLongs: point.liquidatedLongs * rate,
        liquidatedShorts: point.liquidatedShorts * rate,
      };
    });

  return poolInfo ? tradingDataWithRates : data;
}
